import { useEffect } from "react";

import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Typography } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import styled, { useTheme } from "styled-components";

import { usePutPinMessage, usePutUnpinMessage } from "api/services/messaging/hooks";
import type { Message, PinnedMessages } from "api/services/messaging/schemas/messaging";
import { MUIPositionedMenu } from "shared/atoms/MUIPositionedMenu";
import { RelativeDatetime } from "shared/atoms/RelativeDatetime";
import { reportError } from "utils/errorReporting";

import { ChatBubble } from "./styles";

interface TextMessageProps {
  message: Pick<Message, "body" | "created_at" | "id">;
  pinnedMessages?: PinnedMessages;
  isOutbound: boolean;
}

export const TextMessage: React.FC<TextMessageProps> = ({ message, pinnedMessages, isOutbound }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const putPinMessage = usePutPinMessage();
  const putUnpinMessage = usePutUnpinMessage();

  useEffect(() => {
    return () => {
      if (pinnedMessages?.find(pinnedMessage => pinnedMessage.id === message.id)) {
        putUnpinMessage.mutateAsync(message.id, {
          onSuccess: () => {
            queryClient.invalidateQueries("getConversations");
          },
        });
      }
    };
  }, []);

  const handlePinMessage = () => {
    const messagesToUnpin = pinnedMessages?.filter(pinnedMessage => pinnedMessage.id !== message.id) || [];
    messagesToUnpin.forEach(pinnedMessage => {
      putUnpinMessage.mutate(pinnedMessage.id, {
        onError: error => reportError("TextMessage handlePinMessage error", new Error(JSON.stringify(error))),
      });
    });
    putPinMessage.mutate(message.id, {
      onSuccess: () => queryClient.invalidateQueries("getConversations"),
      onError: error => reportError("TextMessage handlePinMessage error", new Error(JSON.stringify(error))),
    });
  };

  return (
    <>
      {message.id === pinnedMessages?.find(pinnedMessage => pinnedMessage.id === message.id)?.id && (
        <NewMessageIndicator>
          <Typography variant="subtitle2" textTransform="uppercase" color={theme.colors.redesign.b100}>
            {t("common.new")[1]}
          </Typography>
          <div className="line" />
        </NewMessageIndicator>
      )}
      <ChatBubble data-testid="chat-bubble" className={clsx({ inbound: !isOutbound, outbound: isOutbound })}>
        <Typography variant="body1" whiteSpace="pre-wrap" sx={{ fontWeight: 300 }}>
          {message.body}
        </Typography>
        {!isOutbound && (
          <MUIPositionedMenu
            renderButton={onClick => (
              <IconButton
                data-testid="MarkAsUnread-menu-button"
                onClick={onClick}
                sx={{ height: "16px", width: "16px", padding: 0, position: "absolute", top: 16, right: 20 }}
              >
                <FontAwesomeIcon icon={faEllipsis} color={theme.colors.redesign.db100} />
              </IconButton>
            )}
            options={[{ label: t("button.mark_as_unread"), value: "pin", dataTestId: "MarkAsUnread-button" }]}
            handleMenuItemClick={handlePinMessage}
          />
        )}
        {message.created_at && (
          <Typography
            variant="label"
            color={!isOutbound ? theme.colors.redesign.db60 : theme.colors.white}
            sx={{ alignSelf: isOutbound ? "flex-end" : "flex-start" }}
          >
            <RelativeDatetime dateString={message.created_at} />
          </Typography>
        )}
      </ChatBubble>
    </>
  );
};

const NewMessageIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;

  & > div.line {
    height: 1px;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.redesign.b100};
  }
`;
